import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { fetchAllContent, getImageUrl } from "../../redux/content";
import "../../components/styles/categoryPage.scss";

/**
 * CategoryPage — Genre/Category browse screen
 * Figma: Category (Desktop) (24654:6404) — 1920×1240
 *
 * Handles both:
 *   - Specific category UIDs from the API (e.g., /category/trending)
 *   - Special nav routes: /category/movies (all movies), /category/tv-shows (all series)
 */

const FILTER_TABS = ["All", "Movie", "TV Series"];

// Special routes from Navbar that aren't real category UIDs
const SPECIAL_ROUTES = {
  movies: { label: "Movies", filter: "Movie" },
  "tv-shows": { label: "TV Shows", filter: "TV Series" },
  rent: { label: "Rent", filter: "All" },
};

const CategoryPage = () => {
  const { name, genre } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isRentRoute = location.pathname.startsWith("/rent");
  const routeName = isRentRoute ? (genre || "rent") : name;
  const [activeFilter, setActiveFilter] = useState("All");
  console.log('[CategoryPage] rendering, route:', routeName, 'isRent:', isRentRoute, 'genre:', genre);

  // Redux state
  const { categories, moviesByCategories, loaders } = useSelector(
    (state) => state.content
  );

  // Fetch content if not loaded yet
  useEffect(() => {
    if (!categories || categories.length === 0) {
      fetchAllContent();
    }
  }, [categories]);

  // Set default filter for special routes
  useEffect(() => {
    const special = SPECIAL_ROUTES[(routeName || "").toLowerCase()];
    if (special) {
      setActiveFilter(special.filter);
    } else {
      setActiveFilter("All");
    }
  }, [routeName]);

  // Safe string extraction
  const safeStr = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") return val.uid || val.name || val.value || "";
    return String(val);
  };

  const categoryKey = (routeName || "").toLowerCase();
  const isSpecialRoute = !!SPECIAL_ROUTES[categoryKey];

  // For special routes (movies / tv-shows), gather ALL content from ALL categories
  // For specific category UIDs, just use that category's content
  const getAllContent = () => {
    if (!moviesByCategories) return [];

    if (isSpecialRoute) {
      // Merge all movies from all categories, deduplicate by id
      const seen = new Set();
      const merged = [];
      Object.values(moviesByCategories).forEach((items) => {
        (items || []).forEach((item) => {
          const key = item.id || item.uid;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(item);
          }
        });
      });
      return merged;
    }

    // Specific category
    return moviesByCategories[categoryKey] || [];
  };

  const allItems = getAllContent();

  // Pick a random poster from this category's content for the hero background
  const heroPoster = useMemo(() => {
    if (!allItems || allItems.length === 0) return null;
    const withImages = allItems.filter((item) => getImageUrl(item, "POSTER"));
    if (withImages.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * withImages.length);
    return getImageUrl(withImages[randomIndex], "POSTER");
  }, [allItems]);

  // Determine display name
  const categoryDisplayName = (() => {
    const special = SPECIAL_ROUTES[categoryKey];
    if (special) return special.label;

    const category = (categories || []).find(
      (cat) => (cat.uid || "").toLowerCase() === categoryKey
    );
    if (category) return category.name || category.uid;

    return decodeURIComponent(routeName || "Category");
  })();

  // Determine active navbar tab
  const navActiveTab = (() => {
    if (categoryKey === "movies") return "Movies";
    if (categoryKey === "tv-shows") return "TV Shows";
    if (categoryKey === "rent") return "Rent";
    return "";
  })();

  // Filter by type
  const filteredItems = allItems.filter((item) => {
    if (activeFilter === "All") return true;
    if (activeFilter === "Movie") {
      return !item.series_id && !item.seasons;
    }
    if (activeFilter === "TV Series") {
      return item.series_id || item.seasons;
    }
    return true;
  });

  return (
    <div className="category-page">
      <Navbar activeTab={navActiveTab} />

      {/* Hero Banner — random poster from this category */}
      <section className="category-page__hero">
        {heroPoster ? (
          <img
            src={heroPoster}
            alt=""
            className="category-page__hero-poster"
          />
        ) : (
          <div className="category-page__hero-bg" />
        )}
        <div className="category-page__hero-overlay" />
        <h1 className="category-page__hero-title">{categoryDisplayName}</h1>
      </section>

      <div className="category-page__body">
        {/* Filter Tabs */}
        <div className="category-page__filters">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab}
              className={`category-page__filter-tab ${
                activeFilter === tab
                  ? "category-page__filter-tab--active"
                  : ""
              }`}
              onClick={() => setActiveFilter(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loaders.content && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#a3a3a3" }}>
            Loading...
          </div>
        )}

        {/* Poster Grid */}
        {!loaders.content && filteredItems.length > 0 && (
          <div className="category-page__grid">
            {filteredItems.map((item, i) => (
              <div
                key={item.id || i}
                className="category-page__poster"
                onClick={() => navigate(`/details/${item.id}`, {
                  state: { contentType: item.type || (item.series_id || item.seasons ? "series" : "movie") },
                })}
                style={{ cursor: "pointer" }}
              >
                <div className="category-page__poster-thumb">
                  {getImageUrl(item, "POSTER") ? (
                    <img
                      src={getImageUrl(item, "POSTER")}
                      alt={safeStr(item.title)}
                      className="category-page__poster-img"
                    />
                  ) : (
                    <span className="category-page__poster-title">
                      {safeStr(item.title)}
                    </span>
                  )}
                </div>
                <span className="category-page__poster-label">
                  {safeStr(item.title)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loaders.content && filteredItems.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#a3a3a3" }}>
            No content found in this category.
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
};

export default CategoryPage;
